import { useRoles } from "@app/auth/useRoles";
import { useCurrentUser } from "@app/providers/UserContext";
import {
  APP_NAME,
  BRAND_GRADIENT,
  HEADER_GRADIENT,
  LOGO_URL,
} from "@app/theme/tokens";
import { useAuth } from "@laborelec/lbe-react/auth";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

const VIEWS = [
  { value: "/", label: "Expert view" },
  { value: "/dashboard", label: "Client dashboard" },
];

/** Header shared by the expert app and the client dashboard. */
export default function AppHeader({ actions }: { actions?: React.ReactNode }) {
  const { logout } = useAuth();
  const { isClient } = useRoles();
  const { name, email } = useCurrentUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentView = pathname.startsWith("/dashboard") ? "/dashboard" : "/";

  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.1,
        background: HEADER_GRADIENT,
        boxShadow: "0 2px 14px rgba(0,42,92,0.25)",
        flexShrink: 0,
        zIndex: 1,
      }}
    >
      <Box
        component="img"
        src={LOGO_URL}
        alt="ENGIE Laborelec"
        sx={{ height: 32, width: "auto", display: "block" }}
      />
      <Box
        sx={{
          width: "1px",
          height: 28,
          bgcolor: "rgba(255,255,255,0.35)",
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: "1.3rem",
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: "0.04em",
            background: BRAND_GRADIENT,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {APP_NAME}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.66rem",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.72)",
            display: { xs: "none", md: "block" },
          }}
        >
          Single Point of Failure Intelligence
        </Typography>
      </Box>
      {!isClient && (
        <TextField
          select
          size="small"
          value={currentView}
          onChange={(event) => navigate(event.target.value)}
          sx={{
            minWidth: 180,
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              fontSize: "0.82rem",
              bgcolor: "rgba(255,255,255,0.08)",
              "& fieldset": { borderColor: "rgba(255,255,255,0.4)" },
              "&:hover fieldset": { borderColor: "#fff" },
            },
            "& .MuiSelect-icon": { color: "#fff" },
          }}
        >
          {VIEWS.map((view) => (
            <MenuItem key={view.value} value={view.value}>
              {view.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {actions}
      {name && (
        <Tooltip title={email}>
          <Typography
            sx={{
              color: "#fff",
              fontSize: "0.82rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Typography>
        </Tooltip>
      )}
      <Tooltip title="Logout">
        <Button
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            color: "rgba(255,255,255,0.85)",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.82rem",
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.12)" },
          }}
        >
          Logout
        </Button>
      </Tooltip>
    </Box>
  );
}
